import { ServiceLLMApi } from 'SharedDir/backend/api';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { OffsetParams } from 'SharedDir/declarations/pagination';
import { isEmptyOrNullString } from '@packages/toolbox';
import { ServiceLLMChatMessageObject, ServiceLLMMessage } from 'SharedDir/declarations/service-llm';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useLoading } from 'SharedDir/common/composables/useLoading';
import { ServiceLLMStatus } from 'SharedDir/constants/service-llm';
import useDebounce from 'SharedDir/common/composables/useDebounce';
import useCustomLoading from 'SharedDir/common/composables/useCustomLoading';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { routerMainPageName } from 'PortalDir/router/router.constants';
import { IS_VISIBLE_AI_SEARCH } from 'PortalDir/constants/app/common';

interface ChatWrapper {
  scrollToBottom: (smooth: boolean) => Promise<void>
}

const getNowCreatedAt = () => {
  const now = new Date();

  const createdAt =
    `${now.getUTCFullYear()}-` +
    `${String(now.getUTCMonth() + 1).padStart(2, '0')}-` +
    `${String(now.getUTCDate()).padStart(2, '0')}T` +
    `${String(now.getUTCHours()).padStart(2, '0')}:` +
    `${String(now.getUTCMinutes()).padStart(2, '0')}:` +
    `${String(now.getUTCSeconds()).padStart(2, '0')}.` +
    `${String(now.getUTCMilliseconds()).padStart(3, '0')}000`;

  return createdAt;
};

const defaultSearchChatParams: OffsetParams = {
  limit: 25,
  offset: 0,
  sortBy: 'createdAt',
  showDeleted: false,
  descending: true
};

const DEFAULT_MESSAGE = 'Хочу получить услугу';

const buildSendMessage = (
  message: ServiceLLMChatMessageObject,
  chatId: number,
  isUserQuestion: boolean = true
) => {
  return {
    chatId,
    message,
    isUserQuestion,
    createdAt: getNowCreatedAt(),
  } as ServiceLLMMessage;
};

export function useAISearchChat() {
  const router = useRouter();
  const { action } = useLoading();
  const { withDebounce } = useDebounce(5000);
  const {
    loading: messagesLoading,
    requestOptions: messagesRequestOptions
  } = useCustomLoading();
  const notify = useNotifications();

  const messages = ref<ServiceLLMMessage[]>([]);
  const activeChatId = ref<number | null>(null);
  const messagePaginationParams = ref<OffsetParams>(defaultSearchChatParams);
  const hasMoreMessages = ref(true);

  const chatWrapperRef = ref<ChatWrapper | null>(null);

  const isSendMessage = computed(() => action.state.value);

  // eslint-disable-next-line no-promise-executor-return
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const sortMessages = (arr: ServiceLLMMessage[]) => {
    return arr.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() -
        new Date(b.createdAt).getTime()
    );
  };

  const mergeMessages = (serverMessages: ServiceLLMMessage[]) => {
    const map = new Map(messages.value.map(m => [m.id, m]));

    serverMessages.forEach(msg => {
      map.set(msg.id, msg);
    });

    messages.value = sortMessages(Array.from(map.values()));
  };

  const getChatList = async (params: Partial<OffsetParams>, silent: boolean = false) => {
    const { data } = await makeRequest(() => ServiceLLMApi.getPaginatedChats({
      ...defaultSearchChatParams,
      ...params
    }), {
      useLoader: !silent
    });

    return data;
  };

  const createChat = async (silent: boolean = false) => {
    const r = await makeRequest(
      () => ServiceLLMApi.store(undefined),
      {
        useLoader: !silent
      }
    );

    return r;
  };

  const getMessageList = async (
    chatId: number,
    params: Partial<OffsetParams>,
    silent: boolean = false
  ) => {
    const r = await makeRequest(
      () => ServiceLLMApi.getMessages(
        chatId,
        {
          ...defaultSearchChatParams,
          ...params
        }
      ),
      {
        startLoading: () => messagesRequestOptions.startLoading(),
        finishLoading: () => messagesRequestOptions.finishLoading(),
        useLoader: !silent
      }
    );

    return r;
  };

  const pingMessageList = async () => {
    if (!activeChatId.value) return;

    const POLL_INTERVAL = 2000;
    const MAX_ATTEMPTS = 60;

    let attempts = 0;

    while (attempts < MAX_ATTEMPTS) {
      attempts++;

      // eslint-disable-next-line no-await-in-loop
      const response = await getMessageList(
        activeChatId.value,
        messagePaginationParams.value,
        true
      );

      const { data } = response;

      const hasPending = data.some(
        (m) => m.status === ServiceLLMStatus.pending
      );

      mergeMessages(data);

      if (!hasPending) {
        action.hide();
        return;
      }

      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_INTERVAL);
    }

    action.hide();
  };

  const loadMoreMessages = async (done: (v: unknown) => void) => {
    if (!hasMoreMessages.value) {
      done(true);
      return;
    }

    if (messagesLoading.value || !activeChatId.value) {
      done(false);
      return;
    }

    try {
      messagePaginationParams.value.offset += messagePaginationParams.value.limit;

      const response = await getMessageList(
        activeChatId.value as number,
        messagePaginationParams.value
      );

      const { data } = response;

      mergeMessages(data);

      hasMoreMessages.value = data.length === messagePaginationParams.value.limit;

      done(!hasMoreMessages.value);
    } catch (error) {
      messagePaginationParams.value.offset -= messagePaginationParams.value.limit;
      done(false);
    }
  };

  const sendMessage = async (message: string | null) => {
    if (!message || isEmptyOrNullString(message?.trim()) || !activeChatId.value) {
      return;
    }

    messages.value = sortMessages([
      ...messages.value,
      buildSendMessage({ message }, activeChatId.value)
    ]);

    chatWrapperRef.value?.scrollToBottom(true);

    const newMessage = await makeRequest(
      () => ServiceLLMApi.createMessage(Number(activeChatId.value), String(message)),
      {
        startLoading: () => action.show(),
        finishLoading: () => action.hide(),
      }
    );

    if (newMessage?.status === ServiceLLMStatus.error) {
      notify.failureNotification('При отправке запроса произошла ошибка');
      return;
    }

    if (newMessage?.status === ServiceLLMStatus.canceled) {
      return;
    }

    messages.value = sortMessages([
      ...messages.value,
      buildSendMessage(
        newMessage.message,
        activeChatId.value,
        newMessage.isUserQuestion
      )
    ]);
  };

  const initPage = async (chatId: string | undefined, message: string | undefined) => {
    if (!IS_VISIBLE_AI_SEARCH) {
      router.push({ name: routerMainPageName });
      return;
    }

    const isSilent = true;

    if (!chatId) {
      activeChatId.value = (await createChat(isSilent)).id;

      await router.replace({
        name: 'search-view',
        query: {
          chatId: activeChatId.value
        }
      });
    } else {
      activeChatId.value = +chatId;
    }

    const messageResponse = await getMessageList(
      activeChatId.value,
      messagePaginationParams.value,
      !isSilent
    );

    const { data, ...paginationParams } = messageResponse;

    const hasSendMessage = data.some((e) => e.status === ServiceLLMStatus.pending);

    if (hasSendMessage) {
      action.show();
      pingMessageList();
    }

    messages.value = sortMessages(data);
    messagePaginationParams.value = paginationParams;
    hasMoreMessages.value = data.length === messagePaginationParams.value.limit;

    if (message && !hasSendMessage) {
      await router.replace({
        name: 'search-view',
        query: {
          chatId: activeChatId.value
        }
      });
      sendMessage(message);
    }
  };

  const openAISearch = async (message: string | null) => {
    message = isEmptyOrNullString(message) ? DEFAULT_MESSAGE : message;

    const chat = await createChat();

    router.push({ name: 'search-view', query: { chatId: chat.id, message } });
  };

  const onCancelMessage = async () => {
    if (!activeChatId.value) return;

    action.hide();

    await makeRequest(
      () => ServiceLLMApi.cancelMessage(Number(activeChatId.value)),
      {
        useLoader: false
      }
    );
  };

  const onDeleteChat = async () => {
    if (!activeChatId.value) return;

    const isSilent = true;

    await ServiceLLMApi.delete(Number(activeChatId.value));
    activeChatId.value = (await createChat(isSilent)).id;

    messages.value = [];
    messagePaginationParams.value.offset = 0;
    hasMoreMessages.value = false;

    router.replace({
      name: 'search-view',
      query: {
        chatId: activeChatId.value
      }
    });
  };

  return {
    chatWrapperRef,
    activeChatId,
    messages,
    messagePaginationParams,

    isSendMessage,
    isVisibleAISearch: IS_VISIBLE_AI_SEARCH,

    openAISearch,
    initPage,
    loadMoreMessages,
    sendMessage,
    onCancelMessage,
    onDeleteChat
  };
}
